import { Button, Modal, useToast } from "@design";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { SpaceSummary } from "@/api";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { useEditableSpaces } from "../inbox/useInbox";
import { useAcceptAll, useImportErrorMessage } from "./useImports";

const VISIBILITIES = ["private", "members", "org"] as const;
const BULK_LIMIT = 500;

/**
 * Accept every pending fact of one import as suggested, into a space the
 * caller can edit (default: the import's). Other names are never written in
 * bulk (they change what links resolve to). Mount only while open.
 */
export function AcceptAllModal({
  importUuid,
  filename,
  count,
  defaultSpaceUuid,
  onClose,
}: Readonly<{ importUuid: string; filename: string; count: number; defaultSpaceUuid: string; onClose: () => void }>) {
  const { t } = useTranslation("knowledge");
  const spaces = useEditableSpaces();
  if (!spaces.data) return null;
  return (
    <AcceptAllForm
      importUuid={importUuid}
      filename={filename}
      count={count}
      spaces={spaces.data}
      defaultSpaceUuid={
        spaces.data.find((s) => s.uuid === defaultSpaceUuid)?.uuid ?? spaces.data.find((s) => s.isPersonal)?.uuid ?? ""
      }
      title={t("import.acceptAllTitle", { filename })}
      onClose={onClose}
    />
  );
}

function AcceptAllForm({
  importUuid,
  count,
  spaces,
  defaultSpaceUuid,
  title,
  onClose,
}: Readonly<{
  importUuid: string;
  filename: string;
  count: number;
  spaces: SpaceSummary[];
  defaultSpaceUuid: string;
  title: string;
  onClose: () => void;
}>) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const message = useImportErrorMessage();
  const accept = useAcceptAll();
  const visibilityFor = (uuid: string) => (spaces.find((s) => s.uuid === uuid)?.isPersonal ? "private" : "members");

  const form = useAppForm({
    defaultValues: {
      spaceUuid: defaultSpaceUuid,
      newPageVisibility: visibilityFor(defaultSpaceUuid) as (typeof VISIBILITIES)[number],
    },
    validators: { onChange: z.object({ spaceUuid: z.string().min(1), newPageVisibility: z.enum(VISIBILITIES) }) },
    onSubmit: async ({ value }) => {
      try {
        const res = await accept.mutateAsync({
          path: { import_uuid: importUuid },
          body: value,
          throwOnError: true,
        });
        const space = spaces.find((s) => s.uuid === res.data.spaceUuid)?.name ?? "";
        toast.success(t("import.acceptedAll", { count: res.data.accepted, space }));
        if (res.data.skipped > 0) toast.info(t("import.skipped", { count: res.data.skipped }));
        onClose();
      } catch (error) {
        toast.error(message(error, t("import.bulkFailed")));
      }
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("import.cancel")}
          </Button>
          <form.Subscribe selector={(s) => s.isSubmitting}>
            {(submitting) => (
              <Button variant="primary" loading={submitting} onClick={() => void form.handleSubmit()}>
                {t("import.acceptAllSubmit")}
              </Button>
            )}
          </form.Subscribe>
        </>
      }
    >
      <form
        aria-label={title}
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <Text tone="secondary">{t("import.acceptAllBody", { count })}</Text>
        {count > BULK_LIMIT && (
          <Text variant="body-sm" tone="secondary">
            {t("import.acceptAllLimit")}
          </Text>
        )}
        <form.AppField
          name="spaceUuid"
          listeners={{ onChange: ({ value }) => form.setFieldValue("newPageVisibility", visibilityFor(value)) }}
        >
          {(field) => (
            <field.SelectField
              label={t("accept.space")}
              options={spaces.map((s) => ({ value: s.uuid, label: s.name }))}
            />
          )}
        </form.AppField>
        <form.AppField name="newPageVisibility">
          {(field) => (
            <field.SelectField
              label={t("accept.visibility")}
              options={VISIBILITIES.map((v) => ({ value: v, label: t(`visibility.${v}`) }))}
            />
          )}
        </form.AppField>
      </form>
    </Modal>
  );
}
